// Consolidated app state hook
// Extracted from App.js to reduce file size

import { useState, useEffect } from 'react';
import useResizable from './useResizable';

// Breakpoint for desktop vs mobile
const DESKTOP_BREAKPOINT = 768;

/**
 * Manages UI state for the app (modals, panels, view mode, etc.)
 */
export function useUIState() {
  const [showSplash, setShowSplash] = useState(() => {
    return !localStorage.getItem('mabel-splash-seen');
  });
  
  const [viewMode, setViewMode] = useState('hub'); // 'hub' | 'focus' | 'plan' | 'roadmap'
  const [showMenu, setShowMenu] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Modal states
  const [editingTask, setEditingTask] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [showMemorySettings, setShowMemorySettings] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  
  // Task list UI state
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [editBuffer, setEditBuffer] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  
  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInitialMessage, setChatInitialMessage] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_BREAKPOINT : true
  );
  
  // Chat panel resizing
  const { 
    width: chatWidth, 
    isResizing: chatIsResizing, 
    showHint: chatShowResizeHint, 
    startResize: chatStartResize 
  } = useResizable({
    storageKey: 'mabel-chat-width',
    hintStorageKey: 'mabel-resize-hint-seen',
    defaultWidth: 380,
    minWidth: 300,
    maxWidthRatio: 0.5
  });

  // Track window size for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleCloseSplash = () => {
    localStorage.setItem('mabel-splash-seen', 'true');
    setShowSplash(false);
  };

  return {
    // View state
    showSplash,
    setShowSplash,
    handleCloseSplash,
    viewMode,
    setViewMode,
    currentTime,
    
    // Menu/filter state
    showMenu,
    setShowMenu,
    expandedFilter,
    setExpandedFilter,
    
    // Card state
    isCardFlipped,
    setIsCardFlipped,
    
    // Modal states
    editingTask,
    setEditingTask,
    showImportModal,
    setShowImportModal,
    importText,
    setImportText,
    showMemorySettings,
    setShowMemorySettings,
    showConsentDialog,
    setShowConsentDialog,
    showReviewModal,
    setShowReviewModal,
    showActivityLog,
    setShowActivityLog,
    
    // Task list UI
    expandedTaskId,
    setExpandedTaskId,
    editBuffer,
    setEditBuffer,
    newTaskTitle,
    setNewTaskTitle,
    
    // Chat
    chatOpen,
    setChatOpen,
    chatInitialMessage,
    setChatInitialMessage,
    
    // Chat panel sizing
    isDesktop,
    chatWidth,
    chatIsResizing,
    chatShowResizeHint,
    chatStartResize
  };
}

/**
 * Manages filter state (capacity, status, starred)
 */
export function useFilterState() {
  const [capacity, setCapacity] = useState({
    priority: [],
    area: [],
    project: [],
    release: [],
    workflow: [],
    timeOfDay: [],
    physical: [],
    mental: [],
    social: []
  });
  const [statusFilter, setStatusFilter] = useState('active');
  const [showStarredOnly, setShowStarredOnly] = useState(false);

  return {
    capacity,
    setCapacity,
    statusFilter,
    setStatusFilter,
    showStarredOnly,
    setShowStarredOnly
  };
}

/**
 * Manages user state with localStorage persistence
 */
export function useUserState() {
  const [userState, setUserState] = useState(() => {
    const saved = localStorage.getItem('mabel-user-state');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        // ignore
      }
    }
    return {
      mood: null,
      energy: null,
      intents: null,  // Array of focus intents (was 'focus')
      location: null,
      timestamp: null
    };
  });

  // Persist user state to localStorage
  useEffect(() => {
    localStorage.setItem('mabel-user-state', JSON.stringify(userState));
  }, [userState]);

  return { userState, setUserState };
}

/**
 * Manages chat messages with localStorage persistence
 */
export function useChatMessages() {
  const [chatMessages, setChatMessages] = useState(() => {
    const saved = localStorage.getItem('able-chat-history');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  // Persist chat messages to localStorage
  useEffect(() => {
    localStorage.setItem('able-chat-history', JSON.stringify(chatMessages));
  }, [chatMessages]);

  return { chatMessages, setChatMessages };
}

/**
 * Manages pending review counts
 */
export function useReviewCounts(isAuthenticated, getToken) {
  const [pendingFactsCount, setPendingFactsCount] = useState(0);
  const [pendingAliasesCount, setPendingAliasesCount] = useState(0);

  return {
    pendingFactsCount,
    setPendingFactsCount,
    pendingAliasesCount,
    setPendingAliasesCount
  };
}
